import {
    Box,
    Button,
    Divider,
    Drawer,
    Link,
    Stack,
    Typography
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import BrandLogo from '@/components/BrandLogo'
import { useBranding } from '@/contexts/BrandingContext'
import { isBetaMode as isBeta } from '@/lib/betaMode'
import {
    FEATURES,
    FEATURES_PATH,
    featureHref
} from '@/pages/marketing/features/registry.js'
import {
    getVisibleSegments,
    showsPlatformPricing
} from '@/pages/marketing/segments/registry.js'

const linkSx = { '&:hover': { color: 'primary.main' } }

const MainSidebar = (props) => {
    const { onMobileClose, openMobile } = props
    const location = useLocation()
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up('lg'))
    const { brand } = useBranding()
    const isPartnerHost = Boolean(brand?.is_partner)

    // Mirrors MainNavbar: same links, same host gating.
    const segments = getVisibleSegments(isPartnerHost, brand?.brand_key)
    // `&& !isBeta` because a beta build does not MOUNT /pricing (buildPublicRoutes
    // returns nothing), so advertising it here rendered a link straight to the
    // catch-all 404. Visibility and mounting now read the same flag.
    const showPricing = showsPlatformPricing(isPartnerHost) && !isBeta

    // Hash as well as pathname: the feature links below differ only by hash, so without it the
    // drawer would stay open over the section it just scrolled to.
    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose()
        }
    }, [location.pathname, location.hash])

    return (
        <Drawer
            anchor="left"
            onClose={onMobileClose}
            open={!lgUp && openMobile}
            variant="temporary"
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: 'background.default',
                        width: 256
                    }
                }
            }}
        >
            <Box
                sx={{
                    alignItems: 'flex-start',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    p: 2
                }}
            >
                {/* BrandLogo, not the old NGLogo mark: it resolves the active brand, so a
                    partner's logo appears here too rather than the platform's. */}
                <Box component={RouterLink} to="/" sx={{ display: 'flex' }}>
                    <BrandLogo />
                </Box>

                {/* Mirrors the navbar's Features dropdown, expanded — a drawer has the room,
                    and a nested menu on a touch screen does not earn its complexity. */}
                <Typography
                    color="text.secondary"
                    sx={{ mt: 3, mb: 1 }}
                    variant="overline"
                >
                    Features
                </Typography>
                <Stack spacing={1.5} sx={{ width: '100%' }}>
                    <Link
                        color="textSecondary"
                        component={RouterLink}
                        sx={linkSx}
                        to={FEATURES_PATH}
                        underline="none"
                        variant="body1"
                    >
                        All features
                    </Link>
                    {FEATURES.map((feature) => (
                        <Link
                            key={feature.key}
                            color="textSecondary"
                            component={RouterLink}
                            sx={linkSx}
                            to={featureHref(feature.key)}
                            underline="none"
                            variant="body2"
                        >
                            {feature.navTitle}
                        </Link>
                    ))}
                </Stack>

                <Divider sx={{ my: 2.5, width: '100%' }} />

                <Typography
                    color="text.secondary"
                    sx={{ mb: 1 }}
                    variant="overline"
                >
                    Who it&apos;s for
                </Typography>
                <Stack spacing={1.5} sx={{ width: '100%' }}>
                    {segments.map((segment) => (
                        <Link
                            key={segment.key}
                            color="textSecondary"
                            component={RouterLink}
                            sx={linkSx}
                            to={segment.path}
                            underline="none"
                            variant="body1"
                        >
                            {segment.chooserTitle}
                        </Link>
                    ))}
                </Stack>

                <Divider sx={{ my: 2.5, width: '100%' }} />

                <Stack spacing={1.5} sx={{ width: '100%' }}>
                    {showPricing && (
                        <Link
                            color="textSecondary"
                            component={RouterLink}
                            sx={linkSx}
                            to="/pricing"
                            underline="none"
                            variant="body1"
                        >
                            Pricing
                        </Link>
                    )}
                    <Link
                        color="textSecondary"
                        component={RouterLink}
                        sx={linkSx}
                        to="/contact"
                        underline="none"
                        variant="body1"
                    >
                        Contact
                    </Link>
                </Stack>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    component={RouterLink}
                    fullWidth
                    sx={{ borderRadius: 2 }}
                    to="/login"
                    variant="contained"
                >
                    Sign in
                </Button>
            </Box>
        </Drawer>
    )
}

MainSidebar.propTypes = {
    onMobileClose: PropTypes.func,
    openMobile: PropTypes.bool
}

export default MainSidebar
